import type { z } from 'zod';

/**
 * Object-level issues carry an empty path. Collected rather than dropped; a
 * consumer whose schema can raise one is responsible for rendering it.
 */
export const FORM_ERROR = '_form' as const;

export type FieldErrors<T> = Partial<Record<(keyof T & string) | typeof FORM_ERROR, string>>;

/**
 * First issue per field from a safeParse result, for cards that gate a button on
 * `.success` and would otherwise throw the messages away.
 *
 * The result has a null prototype: read it by property or spread it, but never
 * `Object.assign` it into a `{}` literal — that goes through [[Set]] and would drop a
 * field named `__proto__`.
 */
export const fieldErrorsOf = <T>(result: z.ZodSafeParseResult<T>): FieldErrors<T> => {
  if (result.success) return Object.create(null);
  // Object.create(null): a field named `constructor` or `toString` is truthy on a
  // plain literal, so its message would be silently swallowed.
  const errors: Record<string, string> = Object.create(null);
  result.error.issues.forEach((issue) => {
    const field = String(issue.path[0] ?? FORM_ERROR);
    if (!errors[field]) errors[field] = issue.message;
  });
  return errors as FieldErrors<T>;
};
